import { Alert, Group, SimpleGrid, Skeleton, Stack } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { TbInfoCircle } from 'react-icons/tb'

import PageShell from '@/components/Common/PageShell'
import { SectionTitle } from '@/components/Common/SectionTitle'
import { BillingActionsMenu } from './BillingActionsMenu'
import { CumulativeBillingChart, DailyIncrementsChart } from './BillingCharts'
import { BillingDistrictTable } from './BillingDistrictTable'
import { BillingPeriodSelect } from './BillingPeriodSelect'
import { AssumptionsPopover, BillingSummary, PeriodDayBadge } from './BillingSummary'
import { useBillingOverview } from './useBillingOverview'

export const BillingOverviewPage = ({ billingPeriodId }: { billingPeriodId: number }) => {
  const { t } = useTranslation('billing')
  const { forecast, districts, periods, timezone, hasHistory } = useBillingOverview(billingPeriodId)

  return (
    <PageShell
      title={
        <Group gap="sm" align="center">
          <SectionTitle order={1} mb={0}>
            {t('title')}
          </SectionTitle>
          <PeriodDayBadge forecast={forecast} />
          <AssumptionsPopover timezone={timezone} />
        </Group>
      }
      fullWidth
      action={
        <Group gap="xs" wrap="nowrap">
          <BillingPeriodSelect periods={periods} value={billingPeriodId} />
          <BillingActionsMenu billingPeriodId={billingPeriodId} />
        </Group>
      }
    >
      <Stack gap="lg">
        {!hasHistory && (
          <Alert color="gray" icon={<TbInfoCircle />} title={t('emptyHistoryTitle')}>
            {t('emptyHistoryMessage')}
          </Alert>
        )}
        <BillingSummary forecast={forecast} />
        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="sm">
          <CumulativeBillingChart forecast={forecast} />
          <DailyIncrementsChart forecast={forecast} />
        </SimpleGrid>
        <BillingDistrictTable rows={districts} billingPeriodId={billingPeriodId} />
      </Stack>
    </PageShell>
  )
}

export const BillingOverviewLoading = () => {
  const { t } = useTranslation('billing')

  return (
    <PageShell title={t('title')} fullWidth action={<Skeleton h={36} w={280} />}>
      <Stack gap="lg">
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
          {[0, 1, 2].map(index => (
            <Skeleton key={index} h={120} radius="md" />
          ))}
        </SimpleGrid>
        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="sm">
          <Skeleton h={360} radius="md" />
          <Skeleton h={360} radius="md" />
        </SimpleGrid>
      </Stack>
    </PageShell>
  )
}

export default BillingOverviewPage
